import React, { createContext, useCallback, useState, useContext } from 'react';
import {
  loginUser,
  requestAdminOtp,
  verifyAdminOtp,
  fetchUsers as apiFetchUsers,
  createUser as apiCreateUser,
  updateUser as apiUpdateUser,
  deleteUser as apiDeleteUser,
} from '../api/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAdminUserId, setPendingAdminUserId] = useState(null);

  const login = async (userId, password) => {
    setIsLoading(true);
    try {
      const res = await loginUser(userId, password);
      
      const userData = {
        id: res.user?.id,
        userId: res.user?.userId || res.user?.user_id,
        name: res.user?.name,
        email: res.user?.email,
        role: res.user?.role,
        token: res.token || res.accessToken,
      };
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginAdmin = async (userId) => {
    setIsLoading(true);
    try {
      const data = await requestAdminOtp(userId);
      if (!data.success) {
        throw new Error(data.message || 'Failed to send OTP');
      }
      setPendingAdminUserId(userId);
      return { maskedPhone: data.phone || '' };
    } catch (err) {
      const message = err.message || 'Failed to send OTP';
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const completeAdminLogin = async (otp) => {
    if (!pendingAdminUserId) throw new Error('No pending admin login. Please enter your User ID again.');
    setIsLoading(true);
    try {
      const data = await verifyAdminOtp(pendingAdminUserId, otp);
      if (!data.success || !data.token || !data.user) {
        throw new Error(data.message || 'Invalid OTP');
      }
      
      const userData = {
        id: String(data.user.id),
        userId: data.user.userId,
        name: data.user.name || '',
        email: data.user.email || '',
        role: data.user.role?.toLowerCase() || 'admin',
        token: data.token,
      };
      
      setUser(userData);
      setPendingAdminUserId(null);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      const message = err.message || 'Invalid OTP';
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setUsers([]);
    localStorage.removeItem('user');
  };

  const getAllUsers = useCallback(async (role) => {
    setIsLoading(true);
    try {
      const list = await apiFetchUsers(role);
      // Map database fields (snake_case) to camelCase
      const mappedUsers = list.map((u) => ({
        id: String(u.id),
        userId: u.user_id ?? u.userId ?? '',
        name: u.name || '',
        email: u.email || '',
        phone: u.phone || '',
        role: u.role?.toLowerCase() || 'user',
        isActive: u.is_active ?? u.isActive ?? true,
        siteName: u.site_name ?? u.siteName,
        siteAddress: u.site_address ?? u.siteAddress,
        siteType: u.site_type ?? u.siteType,
        systemType: u.system_type ?? u.systemType,
        carCount: u.car_count ?? u.carCount,
        systemQuantity: u.system_quantity ?? u.systemQuantity,
        state: u.state,
        area: u.area,
        areaHeadId: u.area_head_id ?? u.areaHeadId,
        latitude: u.latitude,
        longitude: u.longitude,
        createdAt: u.created_at ?? u.createdAt ?? new Date().toISOString(),
        updatedAt: u.updated_at ?? u.updatedAt,
      }));
      setUsers(mappedUsers);
      return mappedUsers;
    } catch (err) {
      console.error('Failed to fetch users:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createUser = useCallback(async (newUser) => {
    setIsLoading(true);
    try {
      const res = await apiCreateUser(newUser);
      const newUserData = res.data?.data || res.data;
      if (newUserData) {
        setUsers((prev) => [...prev, newUserData]);
      }
      return res;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateUserContext = useCallback(async (id, updates) => {
    setIsLoading(true);
    try {
      const res = await apiUpdateUser(id, updates);
      const data = res.data?.data || res.data;
      if (data) {
        setUsers((prev) =>
          prev.map((u) => (String(u.id) === String(data.id) ? { ...u, ...data } : u))
        );
        if (user?.id === data.id) {
          setUser((prev) => ({ ...prev, ...data }));
        }
      }
      return res;
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const deleteUserContext = useCallback(async (userId) => {
    setIsLoading(true);
    try {
      await apiDeleteUser(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkAuth = () => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        const userData = JSON.parse(stored);
        if (userData.token) {
          setUser(userData);
          return true;
        }
      } catch (e) {}
    }
    return false;
  };

  React.useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        users,
        isAuthenticated: !!user,
        login,
        loginAdmin,
        completeAdminLogin,
        logout,
        isLoading,
        getAllUsers,
        createUser,
        updateUser: updateUserContext,
        deleteUser: deleteUserContext,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
